import { useLazyQuery } from '@apollo/client';
import { Close } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { SEARCH_PRODUCTS_IN_PROVIDER } from '@ovComponents/2-component/searchSecuritiesField/searchSecuritiesField.queries';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../../providers/userContextProvider';
import {
  type RefinitivWidgetType,
  useRefinitivWidget,
} from '../refinitiv/useRefinitivWidget';

type WidgetOptionsContext = {
  ric: string;
  currency?: string;
};

type WidgetConfig = {
  id: string;
  type: RefinitivWidgetType;
  options: (ctx: WidgetOptionsContext) => Record<string, unknown>;
  titleKey?: string;
  panel?: 'left' | 'right';
  stylesheets?: string[];
};

const DEFAULT_WIDGETS: WidgetConfig[] = [
  {
    id: 'instrument-info-security-widget',
    type: 'WInstrumentInfo',
    options: ({ ric }) => ({ ric, buttons: [], showChart: false }),
    panel: 'left',
  },
  {
    id: 'company-profile-security-widget',
    type: 'WCompanyProfile',
    options: ({ ric }) => ({ ric }),
    panel: 'left',
  },
  {
    id: 'price-chart-security-widget',
    type: 'WPriceChart',
    options: ({ ric }) => ({ ric }),
  },
  {
    id: 'news-headlines-security-widget',
    type: 'WNewsHeadlines',
    options: ({ ric }) => ({ symbols: [ric] }),
    titleKey: 'refinitiv.newsHeadlines',
  },
  {
    id: 'estimates-ratios-security-widget',
    type: 'WEstimatesRatios',
    options: ({ ric }) => ({ ric }),
    titleKey: 'refinitiv.estimatesRatios',
  },
  {
    id: 'fund-allocation-security-widget',
    type: 'WFundAllocation',
    options: ({ ric }) => ({ ric }),
    titleKey: 'refinitiv.fundAllocation',
  },
  {
    id: 'fund-avg-annual-returns-security-widget',
    type: 'WFundAvgAnnualReturns',
    options: ({ ric, currency }) => ({
      ric,
      ...(currency && { currency }),
    }),
    titleKey: 'refinitiv.fundAverageAnnualReturns',
  },
  {
    id: 'price-yield-chart-security-widget',
    type: 'WPriceYieldChart',
    options: ({ ric }) => ({ ric }),
    titleKey: 'refinitiv.priceYield',
  },
];

const WIDGETS_BY_SECURITY_TYPE: Record<string, WidgetConfig[]> = {
  STO: [
    {
      id: 'instrument-info-security-widget',
      type: 'WInstrumentInfo',
      options: ({ ric }) => ({ ric, buttons: [], showChart: false }),
      panel: 'left',
    },
    {
      id: 'company-profile-security-widget',
      type: 'WCompanyProfile',
      options: ({ ric }) => ({ ric }),
      panel: 'left',
    },
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'news-headlines-security-widget',
      type: 'WNewsHeadlines',
      options: ({ ric }) => ({ symbols: [ric] }),
      titleKey: 'refinitiv.newsHeadlines',
    },
    {
      id: 'estimates-ratios-security-widget',
      type: 'WEstimatesRatios',
      options: ({ ric }) => ({ ric }),
      titleKey: 'refinitiv.estimatesRatios',
    },
  ],
  FUN: [
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'fund-objective-security-widget',
      type: 'WFundObjective',
      options: ({ ric }) => ({ ric }),
      titleKey: 'refinitiv.fundObjective',
    },
    {
      id: 'fund-allocation-security-widget',
      type: 'WFundAllocation',
      options: ({ ric }) => ({ ric }),
      titleKey: 'refinitiv.fundAllocation',
    },
    {
      id: 'fund-avg-annual-returns-security-widget',
      type: 'WFundAvgAnnualReturns',
      options: ({ ric, currency }) => ({
        ric,
        ...(currency && { currency }),
      }),
      titleKey: 'refinitiv.fundAverageAnnualReturns',
    },
  ],
  OPT: [
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
  ],
  IND: [
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
  ],
  CUR: [
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
  ],
  BON: [
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'price-yield-chart-security-widget',
      type: 'WPriceYieldChart',
      options: ({ ric }) => ({ ric }),
      titleKey: 'refinitiv.priceYield',
    },
  ],
  CER: [
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'price-yield-chart-security-widget',
      type: 'WPriceYieldChart',
      options: ({ ric }) => ({ ric }),
      titleKey: 'refinitiv.priceYield',
    },
  ],
  MON: [
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'price-yield-chart-security-widget',
      type: 'WPriceYieldChart',
      options: ({ ric }) => ({ ric }),
      titleKey: 'refinitiv.priceYield',
    },
  ],
  ECI: [
    {
      id: 'main-index-security-widget',
      type: 'WMainIndex',
      options: ({ ric }) => ({ ric }),
    },
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
  ],
  CRE: [
    {
      id: 'market-tiles-security-widget',
      type: 'WMarketTiles',
      options: ({ ric }) => ({ rics: [ric] }),
      stylesheets: ['/refinitiv/overrides/WMarketTiles-single-tile.css'],
    },
    {
      id: 'price-chart-security-widget',
      type: 'WPriceChart',
      options: ({ ric }) => ({ ric }),
    },
  ],
};

const RefinitivWidget = ({
  id,
  type,
  widgetOptions,
  titleKey,
  stylesheets,
}: {
  id: string;
  type: RefinitivWidgetType;
  widgetOptions: Record<string, unknown>;
  titleKey?: string;
  stylesheets?: string[];
}) => {
  const { t } = useTranslation('pageConfiguration');
  useRefinitivWidget(`#${id}`, type, widgetOptions, stylesheets);

  if (titleKey) {
    return (
      <>
        <Typography variant='headingSmall' mt={2} mb={1}>
          {t(titleKey)}
        </Typography>
        <div id={id} />
      </>
    );
  }

  return <div id={id} />;
};

export const RefinitivSecurityModal = ({
  onClose,
  ricCode,
  securityType,
  currency,
}: {
  onClose: () => void;
  ricCode: string;
  securityType?: string;
  currency?: string;
}) => {
  const { t } = useTranslation('pageConfiguration');
  const { activeOrganization } = useContext(UserContext);
  const [resolvedType, setResolvedType] = useState<string | undefined>(
    securityType,
  );
  const [resolvedCurrency, setResolvedCurrency] = useState<string | undefined>(
    currency,
  );
  const [isResolvingSecurityType, setIsResolvingSecurityType] = useState(
    !securityType,
  );
  const [isCurrencyResolved, setIsCurrencyResolved] = useState(!!currency);

  const [searchProducts] = useLazyQuery(SEARCH_PRODUCTS_IN_PROVIDER);

  useEffect(() => {
    setResolvedType(securityType);
    setResolvedCurrency(currency);

    const needsSecurityTypeResolution = !securityType;
    const needsCurrencyResolution = !currency;

    if (!needsSecurityTypeResolution && !needsCurrencyResolution) {
      setIsResolvingSecurityType(false);
      setIsCurrencyResolved(true);
      return;
    }

    setIsResolvingSecurityType(needsSecurityTypeResolution);
    setIsCurrencyResolved(!needsCurrencyResolution);

    let cancelled = false;

    searchProducts({
      variables: {
        input: { searchText: ricCode, organizationId: activeOrganization.id },
      },
      onCompleted: (data) => {
        if (cancelled) return;

        const suggestions = data?.searchProductsInProvider?.searchSuggestions;
        const match = suggestions?.find(
          (s: { ric?: string }) => s.ric === ricCode,
        );

        if (needsSecurityTypeResolution) {
          setResolvedType(match?.type);
          setIsResolvingSecurityType(false);
        }
        if (needsCurrencyResolution) {
          setResolvedCurrency(match?.currency?.toUpperCase());
          setIsCurrencyResolved(true);
        }
      },
      onError: () => {
        if (cancelled) return;

        if (needsSecurityTypeResolution) {
          setResolvedType(undefined);
          setIsResolvingSecurityType(false);
        }
        if (needsCurrencyResolution) {
          setResolvedCurrency(undefined);
          setIsCurrencyResolved(true);
        }
      },
    });

    return () => {
      cancelled = true;
    };
  }, [ricCode, securityType, currency, activeOrganization.id, searchProducts]);

  const widgetOptionsContext = { ric: ricCode, currency: resolvedCurrency };

  const widgetConfigs = resolvedType
    ? (WIDGETS_BY_SECURITY_TYPE[resolvedType] ?? DEFAULT_WIDGETS)
    : DEFAULT_WIDGETS;
  const leftWidgets = widgetConfigs.filter((w) => w.panel === 'left');
  const rightWidgets = widgetConfigs.filter((w) => w.panel !== 'left');
  const showLeftPanel = leftWidgets.length > 0;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth='xl'>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          {t('refinitiv.securityDetails')} - {ricCode}
          <IconButton onClick={onClose} aria-label='close'>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isResolvingSecurityType || !isCurrencyResolved ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {showLeftPanel && (
              <Grid item xs={12} md={3}>
                {leftWidgets.map((w) => (
                  <RefinitivWidget
                    key={w.id}
                    id={w.id}
                    type={w.type}
                    widgetOptions={w.options(widgetOptionsContext)}
                    titleKey={w.titleKey}
                    stylesheets={w.stylesheets}
                  />
                ))}
              </Grid>
            )}
            <Grid item xs={12} md={showLeftPanel ? 9 : 12}>
              {rightWidgets.map((w) => (
                <RefinitivWidget
                  key={w.id}
                  id={w.id}
                  type={w.type}
                  widgetOptions={w.options(widgetOptionsContext)}
                  titleKey={w.titleKey}
                  stylesheets={w.stylesheets}
                />
              ))}
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};
